import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const SavedSearches = ({ currentFilters, onLoadSearch }) => {
  const [savedSearches, setSavedSearches] = useState([]);
  const [searchName, setSearchName] = useState('');
  const [showSaveForm, setShowSaveForm] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem('savedSearches');
    if (saved) {
      setSavedSearches(JSON.parse(saved));
    }
  }, []);

  const saveCurrentSearch = () => {
    if (!searchName?.trim()) return;

    const newSearch = {
      id: Date.now(),
      name: searchName,
      filters: currentFilters,
      createdAt: new Date()?.toISOString(),
      alertEnabled: false
    };

    const updatedSearches = [...savedSearches, newSearch];
    setSavedSearches(updatedSearches);
    localStorage.setItem('savedSearches', JSON.stringify(updatedSearches));
    
    setSearchName('');
    setShowSaveForm(false);
  };

  const deleteSearch = (searchId) => {
    const updatedSearches = savedSearches?.filter(search => search?.id !== searchId);
    setSavedSearches(updatedSearches);
    localStorage.setItem('savedSearches', JSON.stringify(updatedSearches));
  };

  const toggleAlert = (searchId) => {
    const updatedSearches = savedSearches?.map(search => 
      search?.id === searchId 
        ? { ...search, alertEnabled: !search?.alertEnabled }
        : search
    );
    setSavedSearches(updatedSearches);
    localStorage.setItem('savedSearches', JSON.stringify(updatedSearches));
  };

  const getFilterSummary = (filters) => {
    const parts = [];
    if (filters?.brand) parts?.push(filters?.brand);
    if (filters?.model) parts?.push(filters?.model);
    if (filters?.categories?.length) parts?.push(`${filters?.categories?.length} catégories`);
    if (filters?.minPrice || filters?.maxPrice) {
      parts?.push(`${filters?.minPrice || '0'} - ${filters?.maxPrice || '∞'} FCFA`);
    }
    if (filters?.region) parts?.push(filters?.region);
    
    return parts?.join(' • ') || 'Recherche personnalisée';
  };

  return (
    <div className="space-y-4">
      {/* Save Current Search */}
      <div className="border border-border rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-medium text-foreground">
            Sauvegarder cette recherche / Save this search
          </h4>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowSaveForm(!showSaveForm)}
            iconName={showSaveForm ? "X" : "Plus"}
            iconSize={16}
          >
            {showSaveForm ? 'Annuler' : 'Sauvegarder'}
          </Button>
        </div>

        {showSaveForm && (
          <div className="space-y-3">
            <Input
              label="Nom de la recherche / Search name"
              type="text"
              placeholder="Ex: Pièces Toyota Camry Douala"
              value={searchName}
              onChange={(e) => setSearchName(e?.target?.value)}
              required
            />
            <div className="flex space-x-2">
              <Button
                variant="default"
                size="sm"
                onClick={saveCurrentSearch}
                disabled={!searchName?.trim()}
                iconName="Save"
                iconPosition="left"
                iconSize={14}
              >
                Sauvegarder
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowSaveForm(false)}
              >
                Annuler
              </Button>
            </div>
          </div>
        )}
      </div>
      {/* Saved Searches List */}
      {savedSearches?.length > 0 && (
        <div className="space-y-3">
          <h4 className="font-medium text-foreground">
            Recherches sauvegardées / Saved searches ({savedSearches?.length})
          </h4>
          
          {savedSearches?.map((search) => (
            <div key={search?.id} className="border border-border rounded-lg p-3 hover:bg-muted/50 transition-smooth">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h5 className="font-medium text-foreground mb-1">{search?.name}</h5>
                  <p className="text-sm text-muted-foreground mb-2">
                    {getFilterSummary(search?.filters)}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Créée le {new Date(search.createdAt)?.toLocaleDateString('fr-FR')}
                  </p>
                </div>
                
                <div className="flex items-center space-x-1 ml-3">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => toggleAlert(search?.id)}
                    iconName={search?.alertEnabled ? "Bell" : "BellOff"}
                    iconSize={14}
                    className={search?.alertEnabled ? 'text-accent' : 'text-muted-foreground'}
                    title={search?.alertEnabled ? 'Désactiver les alertes' : 'Activer les alertes'}
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onLoadSearch(search?.filters)}
                    iconName="Search"
                    iconSize={14}
                    title="Charger cette recherche"
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => deleteSearch(search?.id)}
                    iconName="Trash2"
                    iconSize={14}
                    className="text-destructive hover:text-destructive"
                    title="Supprimer"
                  />
                </div>
              </div>
              
              {search?.alertEnabled && (
                <div className="mt-2 p-2 bg-accent/10 border border-accent/20 rounded text-xs text-accent">
                  <Icon name="Bell" size={12} className="inline mr-1" />
                  Alertes activées - Vous recevrez une notification pour les nouvelles annonces correspondantes
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {/* Alert Pricing Info */}
      <div className="bg-muted/50 border border-border rounded-lg p-3">
        <div className="flex items-start space-x-2">
          <Icon name="Info" size={16} className="text-primary mt-0.5" />
          <div className="text-sm">
            <p className="font-medium text-foreground mb-1">
              Alertes payantes / Paid alerts
            </p>
            <p className="text-muted-foreground">
              Les alertes de recherche coûtent 500 FCFA/mois par recherche sauvegardée. 
              Vous recevrez une notification WhatsApp ou SMS pour chaque nouvelle annonce correspondante.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SavedSearches;